import { db } from './index.js';

/**
 * Retourne les rayons d'un magasin triés par ordre de parcours.
 * @param {number} storeId
 * @returns {Promise<Array>}
 */
export async function getAislesByStore(storeId) {
  const aisles = await db.aisles.where('storeId').equals(storeId).toArray();
  return aisles.sort((a, b) => a.order - b.order);
}

/**
 * Retourne un rayon par son id.
 * @param {number} id
 * @returns {Promise<Object|undefined>}
 */
export async function getAisle(id) {
  return db.aisles.get(id);
}

/**
 * Crée un nouveau rayon avec un ordre auto-incrémenté.
 * @param {number} storeId
 * @param {string} name
 * @param {{ order?: number, serverId?: number }} [options]
 * @returns {Promise<number>} - Id du rayon créé
 */
export async function addAisle(storeId, name, { order, serverId } = {}) {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    throw new Error('Le nom du rayon est requis');
  }

  const store = await db.stores.get(storeId);
  if (!store) {
    throw new Error('Magasin non trouvé');
  }

  const existingAisles = await db.aisles.where('storeId').equals(storeId).toArray();
  const maxOrder = existingAisles.reduce((max, aisle) => Math.max(max, aisle.order), -1);

  return db.aisles.add({
    storeId,
    name: trimmedName,
    order: order ?? maxOrder + 1,
    createdAt: new Date(),
    serverId: serverId ?? null,
  });
}

/**
 * Retourne un rayon par son ID serveur.
 * @param {number} storeId
 * @param {number} serverId
 * @returns {Promise<Object|undefined>}
 */
export async function getAisleByServerId(storeId, serverId) {
  return db.aisles
    .where('storeId')
    .equals(storeId)
    .filter((a) => a.serverId === serverId)
    .first();
}

/**
 * Met à jour l'ID serveur d'un rayon local.
 * @param {number} id
 * @param {number} serverId
 */
export async function setAisleServerId(id, serverId) {
  await db.aisles.update(id, { serverId });
}

/**
 * Met à jour un rayon (nom et/ou ordre).
 * @param {number} id
 * @param {{ name?: string, order?: number }} updates
 */
export async function updateAisle(id, { name, order } = {}) {
  const aisle = await db.aisles.get(id);
  if (!aisle) {
    throw new Error('Rayon non trouvé');
  }

  const updates = {};

  if (name !== undefined) {
    const trimmedName = name.trim();
    if (!trimmedName) {
      throw new Error('Le nom du rayon est requis');
    }
    updates.name = trimmedName;
  }

  if (order !== undefined) {
    if (typeof order !== 'number' || order < 0) {
      throw new Error('L\'ordre doit être un nombre positif ou nul');
    }
    updates.order = order;
  }

  if (Object.keys(updates).length > 0) {
    await db.aisles.update(id, updates);
  }
}

/**
 * Supprime un rayon. Les articles et items qui le référençaient passent à aisleId = null.
 * @param {number} id
 */
export async function deleteAisle(id) {
  const aisle = await db.aisles.get(id);
  if (!aisle) {
    throw new Error('Rayon non trouvé');
  }

  await db.transaction('rw', [db.aisles, db.articleTemplates, db.listItems], async () => {
    await db.articleTemplates.where('aisleId').equals(id).modify({ aisleId: null });
    await db.listItems.where('aisleId').equals(id).modify({ aisleId: null });
    await db.aisles.delete(id);
  });
}

/**
 * Réordonne les rayons d'un magasin selon un tableau d'ids.
 * @param {number} storeId
 * @param {number[]} aisleIds - Ids dans le nouvel ordre
 */
export async function reorderAisles(storeId, aisleIds) {
  const aisles = await db.aisles.where('storeId').equals(storeId).toArray();
  const aisleIdSet = new Set(aisles.map((a) => a.id));

  if (aisleIds.length !== aisles.length) {
    throw new Error('Le nombre de rayons ne correspond pas');
  }

  for (const aisleId of aisleIds) {
    if (!aisleIdSet.has(aisleId)) {
      throw new Error(`Rayon ${aisleId} introuvable dans ce magasin`);
    }
  }

  await db.transaction('rw', db.aisles, async () => {
    for (let i = 0; i < aisleIds.length; i++) {
      await db.aisles.update(aisleIds[i], { order: i });
    }
  });
}
